import { canEditLayout } from "../../utils/canEditLayout";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Download, Upload, Loader2, Music } from "lucide-react";
import BlockShell from "./BlockShell";
import { Button } from "../ui/button";
import { useAuth, API } from "../../App";
import { absUrl, shareLink, embedSrcFrom } from "./media";
import ComingSoon from "./ComingSoon";
import { renderInline } from "./renderInline";

const MAX_BYTES = 40 * 1024 * 1024;
const isAudioFile = (f) => /^audio\//.test(f.type) || /\.(mp3|m4a|wav|ogg|aac)$/i.test(f.name);

// Audio clip with on-page upload for admins / scoped editors; visitors get play + Share + Download.
const AudioBlock = ({ block, festival, layoutKey: layoutKeyProp }) => {
  const { user } = useAuth();
  const cfg = block.config || {};
  const layoutKey = layoutKeyProp || (festival?.slug ? `festival:${festival.slug}` : null);
  const canEdit = canEditLayout(user, layoutKey);
  const [audioUrl, setAudioUrl] = useState(cfg.audio_url || "");
  const [busy, setBusy] = useState(false);
  const anchor = `audio-${block.block_id}`;

  const upload = async (file) => {
    if (!file || !layoutKey) return;
    if (!isAudioFile(file)) return toast.error("Pick an audio file (MP3, M4A, WAV or OGG)");
    if (file.size > MAX_BYTES) return toast.error("Audio is over 40MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/audio`, fd, { withCredentials: true });
      await axios.patch(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks/${block.block_id}`, { config: { ...cfg, audio_url: r.data.url } }, { withCredentials: true });
      setAudioUrl(r.data.url);
      toast.success("Audio is live on the page");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Upload failed"); } finally { setBusy(false); }
  };

  const embedSrc = !audioUrl ? embedSrcFrom(cfg.embed_url || cfg.embed_code) : "";
  const comingSoon = !audioUrl && !embedSrc && Boolean(cfg.show_coming_soon);
  if (!audioUrl && !embedSrc && !comingSoon && !canEdit) return null;
  const src = absUrl(audioUrl);
  const Uploader = ({ replace }) => (
    <label className={`inline-flex items-center gap-2 cursor-pointer rounded-full font-semibold text-sm px-4 py-2 border transition-colors ${busy ? "opacity-60 pointer-events-none" : ""} ${replace ? "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200" : "bg-[#0A1626] text-[#F2CC8F] border-[#0A1626] hover:bg-[#1B3A5C]"}`} data-testid="audio-upload-label">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      {busy ? "Uploading…" : replace ? "Replace audio" : "Upload audio (MP3, M4A, WAV)"}
      <input type="file" accept="audio/*,.mp3,.m4a,.wav,.ogg" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid="audio-upload-input" />
    </label>
  );

  return (
    <section id={anchor} className="max-w-7xl mx-auto px-6 md:px-12 py-8 scroll-mt-24" data-testid="fest-audio">
      <BlockShell block={block}>
        {cfg.intro && <p className="text-sm text-[#5C5C5C] mb-4">{cfg.intro}</p>}
        {audioUrl ? (
          <div className="max-w-3xl rounded-2xl border border-[#E2E0DC] bg-white p-5 shadow-sm flex flex-col sm:flex-row gap-5 items-start">
            {cfg.cover && <img src={absUrl(cfg.cover)} alt="" className="w-28 h-28 rounded-xl object-cover border" />}
            <div className="flex-1 w-full">
              {cfg.track_title && <div className="font-extrabold text-[#1A1A1A]">{cfg.track_title}</div>}
              <audio key={audioUrl} src={src} controls preload="metadata" className="w-full mt-2" data-testid="audio-player" />
              {cfg.caption && <p className="text-sm text-[#3a3a3a] mt-3 whitespace-pre-line">{renderInline(cfg.caption, "#1D4ED8")}</p>}
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <Button size="sm" onClick={() => shareLink({ title: cfg.track_title || block.title || "Listen", text: cfg.caption || "", url: `${window.location.origin}${window.location.pathname}#${anchor}` })} className="bg-[#0A1626] hover:bg-[#1B3A5C] text-white rounded-full" data-testid="audio-share"><Share2 className="h-4 w-4 mr-2" /> Share</Button>
                <a href={src} download target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="rounded-full" data-testid="audio-download"><Download className="h-4 w-4 mr-2" /> Download</Button></a>
                {canEdit && <Uploader replace />}
              </div>
            </div>
          </div>
        ) : embedSrc ? (
          <div className="max-w-3xl rounded-2xl border border-[#E2E0DC] bg-white p-5 shadow-sm">
            {cfg.track_title && <div className="font-extrabold text-[#1A1A1A] mb-2">{cfg.track_title}</div>}
            <iframe src={embedSrc} title={cfg.track_title || block.title || "Audio"} className="w-full rounded-xl" style={{ height: Number(cfg.embed_height) || 166 }} allow="autoplay; clipboard-write; encrypted-media" data-testid="audio-embed" />
            {cfg.caption && <p className="text-sm text-[#3a3a3a] mt-3 whitespace-pre-line">{cfg.caption}</p>}
            {canEdit && <div className="mt-3"><Uploader replace /></div>}
          </div>
        ) : comingSoon ? (
          <ComingSoon cfg={cfg} kind="recording">
            {canEdit && <><p className="text-xs text-[#5C5C5C] mb-3">Editors: upload the audio here, or paste a SoundCloud / Bandcamp / YouTube link in <strong>Edit → Embed</strong>.</p><Uploader /></>}
          </ComingSoon>
        ) : (
          <div className="max-w-3xl rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-8 text-center" data-testid="audio-empty-editor">
            <Music className="h-10 w-10 mx-auto text-amber-700 mb-3" />
            <p className="font-extrabold text-[#1A1A1A]">No audio yet</p>
            <p className="text-sm text-[#5C5C5C] mt-1 mb-4">Upload a recording (MP3, M4A, WAV or OGG, up to 40MB). It goes live the moment the upload finishes. Visitors only see this block once there is audio.</p>
            <Uploader />
          </div>
        )}
      </BlockShell>
    </section>
  );
};

export default AudioBlock;
